import { FunctionComponent, useEffect, useState } from 'react';
import { Div, Gutter, Headline, InlineNotification, Loader } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { UserOrganizationResponse, UserOrganizationRequest } from '@cdaas/api-clients/dist/asset-user-openapi';

import { getOrganization, updateOrganization } from '../../utils';
import { BasicInformation } from './BasicInformation';
import UserGroups from './UserGroups/UserGroups';
import { OrganizationPurchaseLimits } from './OrganizationPurchaseLimits/OrganizationPurchaseLimits';

const OrganizationDetails: FunctionComponent = () => {
  const { t } = useTranslation('editOrganization');
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [organization, setOrganization] = useState<UserOrganizationResponse>();
  const [editMode, setEditMode] = useState<null | 'infoCard' | 'purchaseLimitsCard'>(null);

  const fetchOrganization = async () => {
    setLoading(true);
    const organization = await getOrganization();
    if (organization) {
      setOrganization(organization);
    } else {
      setApiError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrganization();
  }, []);

  const saveOrganization = async (updateData: Partial<UserOrganizationRequest>): Promise<boolean> => {
    const organizationUpdateRequest: UserOrganizationRequest = {
      ...organization!,
      ...updateData,
    };
    const updatedOrganization = await updateOrganization(organizationUpdateRequest);
    if (updatedOrganization) {
      setOrganization(updatedOrganization);
      setEditMode(null);
      return true;
    } else {
      return false;
    }
  };

  return (
    <Div style={{ width: '800px', maxWidth: '100%' }}>
      {apiError && (
        <Div alignTextToCenter margin={{ vertical: 'xxlg' }}>
          <InlineNotification variant="error" title={t('organizationSettingsPage.apiError')} />
        </Div>
      )}
      {loading && (
        <Div alignTextToCenter margin={{ vertical: 'xxlg' }}>
          <Loader />
        </Div>
      )}

      {!loading && !apiError && (
        <>
          {!!organization && (
            <>
              <Headline size={'lg'} margin={{ vertical: 'xlg' }} padding={{ vertical: 'sm' }}>
                {t('organizationSettingsPage.headline')}
              </Headline>
              <BasicInformation
                organization={organization}
                showModal={editMode === 'infoCard'}
                onClickEdit={() => setEditMode('infoCard')}
                updateOrganizationBasicInfo={saveOrganization}
                cancelOrganizationBasicInfo={() => setEditMode(null)}
              />
              <Gutter />
              <OrganizationPurchaseLimits
                organization={organization}
                showModal={editMode === 'purchaseLimitsCard'}
                onClickEdit={() => setEditMode('purchaseLimitsCard')}
                updateOrganizationPurchaseLimits={saveOrganization}
                cancelOrganizationPurchaseLimits={() => setEditMode(null)}
              />
              <UserGroups />
            </>
          )}
        </>
      )}
    </Div>
  );
};

export default OrganizationDetails;
